/**
 * Echo derived-calculation engine (EchoPAC-style).
 *
 * Pure functions only — given primary measurements, compute derived values.
 * No DOM, no Redux, no network. The viewer panel subscribes to MeasurementService
 * events and feeds primaries in here; results are written back into the store as
 * rows with source: 'DERIVED'.
 *
 * Formulae follow standard ASE/EACVI conventions. Inputs use the catalog units
 * (cm, cm/s, m/s, mL, mmHg) — see measurementCatalog.ts. Each function returns
 * `null` when a required input is missing, so callers can skip un-computable rows.
 */

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Body surface area (m²). Du Bois by default; Mosteller optional. height cm, weight kg. */
export function bsa(
  heightCm: number | null | undefined,
  weightKg: number | null | undefined,
  method: 'dubois' | 'mosteller' = 'dubois'
): number | null {
  const h = num(heightCm);
  const w = num(weightKg);
  if (h === null || w === null) return null;
  if (method === 'mosteller') return Math.sqrt((h * w) / 3600);
  // Du Bois: 0.007184 * h^0.725 * w^0.425
  return 0.007184 * Math.pow(h, 0.725) * Math.pow(w, 0.425);
}

/**
 * Bernoulli pressure gradient (mmHg) from a distal velocity in m/s.
 *
 * Simplified form ΔP = 4·v2² by default. When a proximal velocity `v1Ms` (e.g. LVOT
 * peak velocity in aortic stenosis, or an upstream velocity in serial stenoses) is
 * supplied and is clinically significant (> ~1.5 m/s per ASE), the expanded form
 * ΔP = 4·(v2² − v1²) is used — the simplified form over-estimates the gradient exactly
 * in the severe cases where V1 is high. Below the threshold V1 is ignored (its
 * contribution is negligible and dropping it matches the simplified convention).
 */
export function bernoulliGradient(
  velocityMs: number | null | undefined,
  v1Ms?: number | null | undefined
): number | null {
  const v2 = num(velocityMs);
  if (v2 === null) return null;
  const v1 = num(v1Ms);
  if (v1 !== null && v1 > 1.5) return 4 * (v2 * v2 - v1 * v1);
  return 4 * v2 * v2;
}

/**
 * Right atrial pressure (mmHg) estimated from IVC diameter + collapsibility.
 * ASE 2025 right heart (Mukherjee, Table 4) assigns RAP from both read together;
 * it gives no value from one of them alone, so a missing input leaves RAP blank
 * (RVSP then uses the intermediate 8 mmHg, see `rvsp`).
 *  - IVC ≤ 2.1 cm AND collapse ≥ 50%  → 3  (range 0–5, normal)
 *  - IVC > 2.1 cm AND collapse < 50%  → 15 (range 10–20, high)
 *  - any other (indeterminate) combo  → 8  (range 5–10, intermediate)
 */
export function rapFromIVC(
  ivcDiamCm: number | null | undefined,
  collapsePct: number | null | undefined
): number | null {
  const d = num(ivcDiamCm);
  const c = num(collapsePct);
  if (d === null || c === null) return null;
  const small = d <= 2.1;
  const bigCollapse = c >= 50;
  if (small && bigCollapse) return 3;
  if (!small && !bigCollapse) return 15;
  return 8;
}

/**
 * RV systolic pressure (≈ PASP) = 4·(TR Vmax)² + RA pressure. TR Vmax in m/s.
 * Without an IVC-based RAP the intermediate 8 mmHg is used (Rudski 2010; ASE right heart 2025).
 */
export function rvsp(
  trVmaxMs: number | null | undefined,
  raPressureMmHg: number | null | undefined = 8
): number | null {
  const grad = bernoulliGradient(trVmaxMs);
  const ra = num(raPressureMmHg) ?? 8;
  if (grad === null) return null;
  return grad + ra;
}

/** LV ejection fraction (%) from end-diastolic / end-systolic volumes (mL). */
export function ejectionFraction(
  edvMl: number | null | undefined,
  esvMl: number | null | undefined
): number | null {
  const edv = num(edvMl);
  const esv = num(esvMl);
  if (edv === null || esv === null || edv === 0) return null;
  return ((edv - esv) / edv) * 100;
}

/** Stroke volume (mL) = LVOT area × LVOT VTI. lvotDiam cm, vti cm. */
export function strokeVolume(
  lvotDiamCm: number | null | undefined,
  lvotVtiCm: number | null | undefined
): number | null {
  const d = num(lvotDiamCm);
  const vti = num(lvotVtiCm);
  if (d === null || vti === null) return null;
  const area = Math.PI * Math.pow(d / 2, 2); // cm²
  return area * vti; // cm³ = mL
}

/** Cardiac output (L/min) = SV (mL) × HR / 1000. */
export function cardiacOutput(
  svMl: number | null | undefined,
  heartRate: number | null | undefined
): number | null {
  const sv = num(svMl);
  const hr = num(heartRate);
  if (sv === null || hr === null) return null;
  return (sv * hr) / 1000;
}

/**
 * Aortic valve area by continuity (cm²) = (LVOT area × LVOT VTI) / AV VTI.
 * lvotDiam cm, lvotVti cm, avVti cm.
 */
export function avaContinuity(
  lvotDiamCm: number | null | undefined,
  lvotVtiCm: number | null | undefined,
  avVtiCm: number | null | undefined
): number | null {
  const sv = strokeVolume(lvotDiamCm, lvotVtiCm);
  const avVti = num(avVtiCm);
  if (sv === null || avVti === null || avVti === 0) return null;
  return sv / avVti;
}

/**
 * Pulmonary-to-systemic flow ratio (Qp/Qs) — shunt quantification.
 * Qp = RVOT stroke volume, Qs = LVOT stroke volume; ratio is dimensionless.
 * Qp/Qs = (RVOT area · RVOT VTI) / (LVOT area · LVOT VTI). Diameters cm, VTI cm.
 * >1 = left-to-right shunt (e.g. ASD/VSD); ~1 = no significant shunt.
 */
export function qpQs(
  lvotDiamCm: number | null | undefined,
  lvotVtiCm: number | null | undefined,
  rvotDiamCm: number | null | undefined,
  rvotVtiCm: number | null | undefined
): number | null {
  const qs = strokeVolume(lvotDiamCm, lvotVtiCm); // systemic
  const qp = strokeVolume(rvotDiamCm, rvotVtiCm); // pulmonary
  if (qs === null || qp === null || qs === 0) return null;
  return qp / qs;
}

/** Mitral valve area by pressure half-time (cm²) = 220 / PHT. PHT in ms. */
export function mvaPressureHalfTime(phtMs: number | null | undefined): number | null {
  const pht = num(phtMs);
  if (pht === null || pht === 0) return null;
  return 220 / pht;
}

/**
 * LV mass (g) by the ASE cube formula:
 * 0.8 · 1.04 · [(LVIDd + IVSd + LVPWd)³ − LVIDd³] + 0.6. All inputs in cm.
 */
export function lvMassCube(
  lvidd: number | null | undefined,
  ivsd: number | null | undefined,
  lvpwd: number | null | undefined
): number | null {
  const d = num(lvidd);
  const s = num(ivsd);
  const w = num(lvpwd);
  if (d === null || s === null || w === null) return null;
  return 0.8 * 1.04 * (Math.pow(d + s + w, 3) - Math.pow(d, 3)) + 0.6;
}

// ── LV volumes by Simpson's method of disks (the EchoPAC standard) ──────────
export type Pt = { x: number; y: number };
export type Pt3 = { x: number; y: number; z: number };

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Project a planar 3D contour (world mm, [x,y,z] arrays or {x,y,z}) onto its own
 * best-fit 2D plane and return 2D points in cm.
 *
 * Why: echo image planes (esp. apical views) are NOT aligned to world X/Y, so taking
 * raw (x,y) collapses the shape and gives ~0 area/volume. Distances are correct only
 * in-plane. We build an in-plane basis (long-axis direction u + an in-plane perpendicular)
 * from the contour itself and express every point in that frame — preserving true physical
 * lengths/areas for the Simpson / disc maths regardless of plane orientation.
 */
const toP3 = (p: any): Pt3 =>
  Array.isArray(p)
    ? { x: p[0] ?? 0, y: p[1] ?? 0, z: p[2] ?? 0 }
    : { x: p?.x ?? 0, y: p?.y ?? 0, z: p?.z ?? 0 };

function planeMapper(raw: any[] | null | undefined, scaleToCm = 0.1): ((p: any) => Pt) | null {
  if (!raw || raw.length < 3) return null;
  const P: Pt3[] = raw.map(toP3);
  const sub = (a: Pt3, b: Pt3): Pt3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
  const dot = (a: Pt3, b: Pt3) => a.x * b.x + a.y * b.y + a.z * b.z;
  const len = (a: Pt3) => Math.hypot(a.x, a.y, a.z);
  const unit = (a: Pt3): Pt3 | null => {
    const l = len(a);
    return l > 1e-9 ? { x: a.x / l, y: a.y / l, z: a.z / l } : null;
  };
  const o = P[0];
  // u = direction to the farthest point (the long axis) — a stable in-plane direction
  let far = P[1],
    md = -1;
  for (const p of P) {
    const d = len(sub(p, o));
    if (d > md) {
      md = d;
      far = p;
    }
  }
  const u = unit(sub(far, o));
  if (!u) return null;
  // in-plane perpendicular = the point with the largest component perpendicular to u
  let perpRef: Pt3 | null = null,
    mp = -1;
  for (const p of P) {
    const v = sub(p, o);
    const proj = dot(v, u);
    const w = { x: v.x - u.x * proj, y: v.y - u.y * proj, z: v.z - u.z * proj };
    const wl = len(w);
    if (wl > mp) {
      mp = wl;
      perpRef = w;
    }
  }
  const pu = perpRef ? unit(perpRef) : null;
  if (!pu) return null;
  return (p: any) => {
    const v = sub(toP3(p), o);
    return { x: dot(v, u) * scaleToCm, y: dot(v, pu) * scaleToCm };
  };
}

export function planarize(raw: any[] | null | undefined, scaleToCm = 0.1): Pt[] | null {
  const map = planeMapper(raw, scaleToCm);
  return map ? (raw as any[]).map(map) : null;
}

/** Long axis of a traced contour = the longest chord between two contour points. */
function longAxis(pts: Pt[]): { p1: Pt; p2: Pt; length: number } | null {
  if (!pts || pts.length < 3) return null;
  let p1 = pts[0],
    p2 = pts[1],
    max = -1;
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const d = dist(pts[i], pts[j]);
      if (d > max) {
        max = d;
        p1 = pts[i];
        p2 = pts[j];
      }
    }
  }
  return max > 0 ? { p1, p2, length: max } : null;
}

/** Manual long-axis endpoints in the SAME space as the contour points. */
export type ManualAxis = { p1: Pt; p2: Pt };

/** A manual axis (drag-corrected apex/base) with its length, or null when unusable. */
function resolveAxis(
  pts: Pt[],
  manual?: ManualAxis | null
): { p1: Pt; p2: Pt; length: number } | null {
  if (manual) {
    const length = dist(manual.p1, manual.p2);
    if (length > 1e-6) return { p1: manual.p1, p2: manual.p2, length };
  }
  return longAxis(pts);
}

/** Per-disk diameters along the long axis (chord width per band). Points in cm.
 *  `manualAxis` (EchoPAC-style drag correction) replaces the longest-chord default. */
function diskDiameters(
  pts: Pt[],
  numDisks: number,
  manualAxis?: ManualAxis | null
): { diameters: number[]; length: number } | null {
  const axis = resolveAxis(pts, manualAxis);
  if (!axis) return null;
  const { p1, length } = axis;
  // unit vector along axis + perpendicular projection
  const ux = (axis.p2.x - p1.x) / length;
  const uy = (axis.p2.y - p1.y) / length;
  const bands: { min: number; max: number }[] = Array.from({ length: numDisks }, () => ({
    min: Infinity,
    max: -Infinity,
  }));
  for (const p of pts) {
    const dx = p.x - p1.x,
      dy = p.y - p1.y;
    const s = dx * ux + dy * uy; // along-axis coord (0..length)
    const perp = dx * -uy + dy * ux; // perpendicular coord (signed)
    let k = Math.floor((s / length) * numDisks);
    if (k < 0) k = 0;
    if (k >= numDisks) k = numDisks - 1;
    if (perp < bands[k].min) bands[k].min = perp;
    if (perp > bands[k].max) bands[k].max = perp;
  }
  const diameters = bands.map(b => (b.max > b.min ? b.max - b.min : 0));
  return { diameters, length };
}

/**
 * Method-of-disks GEOMETRY for drawing the EchoPAC-style disc stack over a trace.
 * Returns the long axis + one evenly-spaced chord per disk, in the SAME coordinate
 * space as the input points (so callers can feed canvas-projected points and draw
 * directly). Pure — no rendering, no Cornerstone. Mirrors diskDiameters' binning.
 */
export function discGeometry(
  pts: Pt[] | null | undefined,
  numDisks = 20,
  manualAxis?: ManualAxis | null
): { axis: { p1: Pt; p2: Pt }; chords: { p1: Pt; p2: Pt }[]; length: number } | null {
  if (!pts || pts.length < 3) return null;
  const axis = resolveAxis(pts, manualAxis);
  if (!axis) return null;
  const { p1, length } = axis;
  const ux = (axis.p2.x - p1.x) / length;
  const uy = (axis.p2.y - p1.y) / length;
  const px = -uy,
    py = ux; // perpendicular unit vector
  // Per band: signed perpendicular min/max → centre + half-width of the chord.
  const bands = Array.from({ length: numDisks }, () => ({ min: Infinity, max: -Infinity }));
  for (const p of pts) {
    const dx = p.x - p1.x,
      dy = p.y - p1.y;
    const s = dx * ux + dy * uy;
    const perp = dx * px + dy * py;
    let k = Math.floor((s / length) * numDisks);
    if (k < 0) k = 0;
    if (k >= numDisks) k = numDisks - 1;
    if (perp < bands[k].min) bands[k].min = perp;
    if (perp > bands[k].max) bands[k].max = perp;
  }
  const chords: { p1: Pt; p2: Pt }[] = [];
  for (let k = 0; k < numDisks; k++) {
    const b = bands[k];
    if (!isFinite(b.min) || !isFinite(b.max) || b.max <= b.min) continue;
    const s = ((k + 0.5) / numDisks) * length; // along-axis centre of the band
    const baseX = p1.x + ux * s,
      baseY = p1.y + uy * s;
    const c = (b.min + b.max) / 2,
      half = (b.max - b.min) / 2;
    chords.push({
      p1: { x: baseX + px * (c - half), y: baseY + py * (c - half) },
      p2: { x: baseX + px * (c + half), y: baseY + py * (c + half) },
    });
  }
  return { axis: { p1: axis.p1, p2: axis.p2 }, chords, length };
}

/**
 * discGeometry in WORLD space: contour + optional manual axis in world coords →
 * axis + chords back in world coords ([x,y,z] arrays).
 *
 * Exists for the disc overlay's render-loop economics: geometry only changes when
 * the ANNOTATION changes, never on pan/zoom/cine — so the overlay computes this
 * once per annotation edit and per frame only projects the ~42 returned endpoints
 * (vs re-projecting the whole dense contour and re-running the O(n²) longest-chord
 * scan at cine rate, which visibly janked the workspace).
 *
 * `maxPts` decimates dense freehand contours before the O(n²) axis scan —
 * geometrically indistinguishable at disc resolution, ~10× cheaper.
 */
export function discGeometryWorld(
  raw: any[] | null | undefined,
  numDisks = 20,
  manualAxisWorld?: { p1: any; p2: any } | null,
  maxPts = 150
): {
  axis: { p1: [number, number, number]; p2: [number, number, number] };
  chords: { p1: [number, number, number]; p2: [number, number, number] }[];
} | null {
  if (!raw || raw.length < 3) return null;
  const stride = Math.max(1, Math.ceil(raw.length / maxPts));
  const sample = stride > 1 ? raw.filter((_: any, i: number) => i % stride === 0) : raw;
  if (sample.length < 3) return null;
  const P: Pt3[] = sample.map(toP3);
  const sub = (a: Pt3, b: Pt3): Pt3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
  const dot3 = (a: Pt3, b: Pt3) => a.x * b.x + a.y * b.y + a.z * b.z;
  const len3 = (a: Pt3) => Math.hypot(a.x, a.y, a.z);
  const unit3 = (a: Pt3): Pt3 | null => {
    const l = len3(a);
    return l > 1e-9 ? { x: a.x / l, y: a.y / l, z: a.z / l } : null;
  };
  const o = P[0];
  let far = P[1],
    md = -1;
  for (const p of P) {
    const d = len3(sub(p, o));
    if (d > md) {
      md = d;
      far = p;
    }
  }
  const u = unit3(sub(far, o));
  if (!u) return null;
  let perpRef: Pt3 | null = null,
    mp = -1;
  for (const p of P) {
    const v = sub(p, o);
    const proj = dot3(v, u);
    const w = { x: v.x - u.x * proj, y: v.y - u.y * proj, z: v.z - u.z * proj };
    const wl = len3(w);
    if (wl > mp) {
      mp = wl;
      perpRef = w;
    }
  }
  const pu = perpRef ? unit3(perpRef) : null;
  if (!pu) return null;
  const to2D = (p: any): Pt => {
    const v = sub(toP3(p), o);
    return { x: dot3(v, u), y: dot3(v, pu) };
  };
  const toWorld = (p: Pt): [number, number, number] => [
    o.x + u.x * p.x + pu.x * p.y,
    o.y + u.y * p.x + pu.y * p.y,
    o.z + u.z * p.x + pu.z * p.y,
  ];
  const pts2 = P.map(p => ({ x: dot3(sub(p, o), u), y: dot3(sub(p, o), pu) }));
  const manual2 = manualAxisWorld
    ? { p1: to2D(manualAxisWorld.p1), p2: to2D(manualAxisWorld.p2) }
    : null;
  const g = discGeometry(pts2, numDisks, manual2);
  if (!g) return null;
  return {
    axis: { p1: toWorld(g.axis.p1), p2: toWorld(g.axis.p2) },
    chords: g.chords.map(c => ({ p1: toWorld(c.p1), p2: toWorld(c.p2) })),
  };
}

/** Polygon area (cm²) by the shoelace formula. Points in cm. */
export function polygonArea(pts: Pt[] | null | undefined): number {
  if (!pts || pts.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(a) / 2;
}

/**
 * Planarize a contour AND calibrate it to a known physical area (cm²).
 *
 * Cornerstone world units aren't reliably mm for ultrasound, so a raw mm→cm scale can be
 * off (gave a ~6–8× too-large LV volume). The drawing tool, however, reports a correctly
 * calibrated area (PhysicalDelta/PixelSpacing). We project to the image plane, then scale
 * the points so the polygon area equals that calibrated area — making every derived length
 * (and thus the Simpson volume) physically correct, independent of world units.
 *
 * Returns `null` when no valid calibrated area is available (rather than silently handing
 * back raw, un-calibrated points): an uncalibrated contour would reintroduce the ~6–8×
 * volume error the calibration exists to prevent. The caller must surface "uncalibrated"
 * instead of emitting a wrong mL.
 */
export function planarizeToArea(
  raw: any[] | null | undefined,
  areaCm2: number | null | undefined
): Pt[] | null {
  return planarizeToAreaWithAxis(raw, areaCm2, null)?.pts ?? null;
}

/**
 * planarizeToArea + carry a manual long axis (world coords) through the SAME
 * projection + area calibration, so a drag-corrected apex/base lands in the
 * exact space the Simpson maths runs in. `axis` null → { pts, axis: null }.
 */
export function planarizeToAreaWithAxis(
  raw: any[] | null | undefined,
  areaCm2: number | null | undefined,
  axisWorld: { p1: any; p2: any } | null | undefined
): { pts: Pt[]; axis: ManualAxis | null } | null {
  const map = planeMapper(raw);
  if (!map) return null;
  const pts = (raw as any[]).map(map);
  if (!areaCm2 || areaCm2 <= 0) return null; // refuse uncalibrated — no wrong mL
  const a = polygonArea(pts);
  if (a <= 0) return null;
  const f = Math.sqrt(areaCm2 / a); // linear scale to match calibrated area
  const cal = (p: Pt): Pt => ({ x: p.x * f, y: p.y * f });
  return {
    pts: pts.map(cal),
    axis: axisWorld ? { p1: cal(map(axisWorld.p1)), p2: cal(map(axisWorld.p2)) } : null,
  };
}

/** LV volume (mL) — single-plane Simpson method of disks from ONE traced contour (cm). */
export function simpsonSinglePlane(
  points: Pt[] | null | undefined,
  numDisks = 20,
  manualAxis?: ManualAxis | null
): number | null {
  if (!points || points.length < 3) return null;
  const d = diskDiameters(points, numDisks, manualAxis);
  if (!d) return null;
  const h = d.length / numDisks; // disk height (cm)
  let v = 0;
  for (const dia of d.diameters) v += (Math.PI / 4) * dia * dia * h;
  return v; // cm³ = mL
}

/** LV volume (mL) — biplane Simpson: V = (π/4)·h·Σ(dA·dB). Both contours in cm. */
export function simpsonBiplane(
  ptsA4C: Pt[] | null | undefined,
  ptsA2C: Pt[] | null | undefined,
  numDisks = 20,
  axisA4C?: ManualAxis | null,
  axisA2C?: ManualAxis | null
): number | null {
  if (!ptsA4C || !ptsA2C) return null;
  const a = diskDiameters(ptsA4C, numDisks, axisA4C);
  const b = diskDiameters(ptsA2C, numDisks, axisA2C);
  if (!a || !b) return null;
  const h = (a.length + b.length) / 2 / numDisks; // averaged long-axis length
  let v = 0;
  for (let k = 0; k < numDisks; k++) v += (Math.PI / 4) * a.diameters[k] * b.diameters[k] * h;
  return v; // mL
}

/**
 * Chamber volume (mL) by the single-plane area-length formula (LA / RA where
 * Simpson isn't used): V = 8·A² / (3·π·L). area cm², length cm.
 */
export function areaLengthVolume(
  areaCm2: number | null | undefined,
  lengthCm: number | null | undefined
): number | null {
  const a = num(areaCm2);
  const l = num(lengthCm);
  if (a === null || l === null || l === 0) return null;
  return (8 * a * a) / (3 * Math.PI * l);
}

/**
 * Chamber volume (mL) by the BIPLANE area-length formula — the ASE-preferred method
 * for the left atrium: V = 8·(A4C·A2C) / (3·π·L), areas cm², L the SHORTER of the two
 * long-axis lengths in cm.
 *
 * Single-plane area-length misreads an asymmetric atrium, and LAVi feeds the diastolic
 * grade, so the error propagates into the grade. `areaLengthVolume` remains the fallback.
 *
 * ALL FOUR inputs are required. ASE specifies measuring the long axis in BOTH views and
 * taking the shorter — a single length cannot satisfy that rule, because if the one
 * measured happens to be the longer of the two it sits in the denominator and the volume
 * comes out low, which is the exact under-reading this method exists to avoid. A missing
 * length returns null so the caller falls back to an honest single-plane result rather
 * than a biplane number the operator did not actually acquire.
 */
export function biplaneAreaLengthVolume(
  a4cAreaCm2: number | null | undefined,
  a2cAreaCm2: number | null | undefined,
  a4cLengthCm: number | null | undefined,
  a2cLengthCm: number | null | undefined
): number | null {
  const a1 = num(a4cAreaCm2);
  const a2 = num(a2cAreaCm2);
  const l1 = num(a4cLengthCm);
  const l2 = num(a2cLengthCm);
  if (a1 === null || a2 === null || l1 === null || l2 === null) {
    return null;
  }
  if (l1 <= 0 || l2 <= 0) {
    return null;
  }

  // ASE takes the SHORTER of the two long-axis lengths.
  const l = Math.min(l1, l2);

  return (8 * a1 * a2) / (3 * Math.PI * l);
}

/** Generic body-surface-area index: value / BSA. Returns null if BSA missing/zero. */
export function index(
  value: number | null | undefined,
  bsaM2: number | null | undefined
): number | null {
  const v = num(value);
  const b = num(bsaM2);
  if (v === null || b === null || b === 0) return null;
  return v / b;
}

/** Simple ratio helper (E/A, E/e'). Returns null if denominator missing/zero. */
export function ratio(
  numerator: number | null | undefined,
  denominator: number | null | undefined
): number | null {
  const n = num(numerator);
  const d = num(denominator);
  if (n === null || d === null || d === 0) return null;
  return n / d;
}

/**
 * Fractional shortening: (LVIDd − LVIDs) / LVIDd × 100. Percent LV cavity
 * shortening in systole (M-mode/2D linear surrogate for EF). Returns null if
 * LVIDd missing/zero. Normal ≈ 25–45 %.
 */
export function fractionalShortening(
  lviddCm: number | null | undefined,
  lvidsCm: number | null | undefined
): number | null {
  const d = num(lviddCm);
  const s = num(lvidsCm);
  if (d === null || s === null || d === 0) return null;
  return ((d - s) / d) * 100;
}

/**
 * RV fractional area change: (EDA − ESA) / EDA × 100. Percent RV cavity area
 * shortening (2D surrogate for RV systolic function, ASE normal ≥ 35 %).
 * Returns null if EDA missing/zero.
 */
export function fractionalAreaChange(
  edaCm2: number | null | undefined,
  esaCm2: number | null | undefined
): number | null {
  const d = num(edaCm2);
  const s = num(esaCm2);
  if (d === null || s === null || d === 0) return null;
  return ((d - s) / d) * 100;
}

/**
 * Relative wall thickness: 2 × LVPWd / LVIDd. Dimensionless. Separates
 * concentric (RWT > 0.42) from eccentric LV remodelling alongside LVMI.
 * Returns null if LVIDd missing/zero.
 */
export function relativeWallThickness(
  lvpwdCm: number | null | undefined,
  lviddCm: number | null | undefined
): number | null {
  const pw = num(lvpwdCm);
  const d = num(lviddCm);
  if (pw === null || d === null || d === 0) return null;
  return (2 * pw) / d;
}

/**
 * Dimensionless index (Doppler velocity index) for aortic stenosis:
 * LVOT VTI / AV VTI. Flow-independent AS severity marker (< 0.25 = severe).
 * Returns null if AV VTI missing/zero.
 */
export function dimensionlessIndex(
  lvotVtiCm: number | null | undefined,
  avVtiCm: number | null | undefined
): number | null {
  const lvot = num(lvotVtiCm);
  const av = num(avVtiCm);
  if (lvot === null || av === null || av === 0) return null;
  return lvot / av;
}

/**
 * Mean pulmonary artery pressure (mmHg) from pulmonary-valve acceleration time (ms).
 * Mahan regression: mPAP = 79 − 0.45·AccT. Mahan under-reads at short acceleration
 * times, so for AccT < 120 ms Dabestani's 90 − 0.62·AccT is used instead. Clamped ≥ 0.
 * Returns null if AccT missing/≤0.  (>20 mmHg = pulmonary hypertension.)
 */
export function meanPAP(pvAccelTimeMs: number | null | undefined): number | null {
  const t = num(pvAccelTimeMs);
  if (t === null || t <= 0) return null;
  const mpap = t < 120 ? 90 - 0.62 * t : 79 - 0.45 * t;
  return mpap > 0 ? mpap : 0;
}

/**
 * LV dP/dt (mmHg/s) from the mitral-regurgitation CW jet: the time (ms) for the jet
 * velocity to rise from 1 to 3 m/s. ΔP over that interval = 4·3² − 4·1² = 32 mmHg, so
 * dP/dt = 32000 / Δt(ms) (Kolias 2000). No guideline cut-off is applied.
 * Returns null if the interval is missing/≤0.
 */
export function lvDpDt(msFrom1to3: number | null | undefined): number | null {
  const dt = num(msFrom1to3);
  if (dt === null || dt <= 0) return null;
  return 32000 / dt;
}

/**
 * Tei / myocardial-performance index = (IVCT + IVRT) / ET. Dimensionless global
 * (systolic + diastolic) function marker. All inputs in ms. Higher = worse
 * (Tei 1995). No LV cut-off is applied. Returns null if any
 * input missing or ET is zero.
 */
export function teiIndex(
  ivctMs: number | null | undefined,
  ivrtMs: number | null | undefined,
  etMs: number | null | undefined
): number | null {
  const a = num(ivctMs);
  const b = num(ivrtMs);
  const et = num(etMs);
  if (a === null || b === null || et === null || et === 0) return null;
  return (a + b) / et;
}

/**
 * RV systolic function from TAPSE (cm). Returns a code:
 * 0 Normal (>1.7 cm), 1 Reduced (≤1.7 cm, ASE right heart 2025). null if missing.
 *
 * The catalog reports TAPSE in cm (the unit every other length uses), so the ASE
 * 17 mm cutoff is 1.7 here. Passing a millimetre value would read as a huge
 * excursion and silently grade Normal — the caller must hand over catalog units.
 */
export function tapseGrade(tapseCm: number | null | undefined): 0 | 1 | null {
  const t = num(tapseCm);
  if (t === null) return null;
  return t > 1.7 ? 0 : 1;
}

/** Diastolic-function grade code: 0 Normal, 1 Grade I, 2 Grade II, 3 Grade III, 9 Indeterminate. */
export type DiastolicGradeCode = 0 | 1 | 2 | 3 | 9;

/** Grade elevated-LAP dysfunction by mitral inflow E/A: ≥2 → Grade 3, else Grade 2. */
function gradeByEA(ea: number | null): DiastolicGradeCode {
  if (ea === null) return 9; // can't separate Grade 2 vs 3 without E/A
  return ea >= 2 ? 3 : 2;
}

/**
 * ASE 2025 diastolic-function assessment (Nagueh et al., JASE 2025) — the general
 * (non-special-population) grading algorithm. Supersedes the 2016 four-criterion
 * approach. Special-population algorithms (atrial fibrillation, pulmonary HTN, HCM,
 * significant MR/MS, MAC, transplant, constriction, LVAD) are NOT handled here — they
 * need clinical context this tool doesn't carry; treat output as advisory in those cases.
 *
 * Three PRIMARY variables (age-independent cutoffs):
 *   1. e′ reduced        — average e′ < 6.5 cm/s (or septal < 6 / lateral < 7 when only one)
 *   2. E/e′ elevated     — average ≥ 14 (septal ≥ 15 / lateral ≥ 13 when only one)
 *   3. TR/PASP elevated  — TR peak ≥ 2.8 m/s  OR  PASP (≈ RVSP) ≥ 35 mmHg
 *
 * SUPPLEMENTAL markers of elevated LA pressure (any ≥ 1 positive ⇒ elevated LAP):
 *   PV S/D ≤ 0.67 · LA reservoir strain (LARS) ≤ 18 % · LAVi > 34 mL/m².
 *   IVRT ≤ 70 ms is the alternative, used only when none of those three is available
 *   (Nagueh 2025, Figure 3).
 *
 * Pathway:
 *   0 abnormal primaries                 → Normal
 *   reduced e′ only, E/A ≤ 0.8           → Grade 1 (impaired relaxation, normal LAP)
 *   reduced e′ only, E/A > 0.8           → supplemental decides Grade 1 vs elevated LAP
 *   1 (E/e′ or TR) or 2 abnormal         → supplemental decides Grade 1 vs elevated LAP
 *   all 3 abnormal                       → elevated LAP
 *   elevated LAP → grade by E/A: < 2 ⇒ Grade 2, ≥ 2 ⇒ Grade 3
 *
 * Returns 9 (Indeterminate) when data is too sparse to decide, null when nothing usable.
 * Units: e′ cm/s, E/e′ ratio, TR m/s, PASP mmHg, LAVi mL/m², LARS %, PV S/D ratio, IVRT ms, E/A ratio.
 */
export function diastolicGrade(i: {
  eSeptal?: number | null;
  eLateral?: number | null;
  eOverEprime?: number | null;
  trVmax?: number | null;
  pasp?: number | null;
  lavi?: number | null;
  larStrain?: number | null;
  pvSD?: number | null;
  ivrt?: number | null;
  eaRatio?: number | null;
  mvE?: number | null;
}): DiastolicGradeCode | null {
  const eSep = num(i.eSeptal ?? null);
  const eLat = num(i.eLateral ?? null);
  const eOverEp = num(i.eOverEprime ?? null);
  const tr = num(i.trVmax ?? null);
  const pasp = num(i.pasp ?? null);

  // Primary 1 — e′ reduced (average preferred; single-annulus cutoff when only one).
  const eLowAvail = eSep !== null || eLat !== null;
  let eLow = false;
  if (eSep !== null && eLat !== null) eLow = (eSep + eLat) / 2 < 6.5;
  else if (eSep !== null) eLow = eSep < 6;
  else if (eLat !== null) eLow = eLat < 7;

  // Primary 2 — E/e′ elevated. The ratio uses the average e′ when both annuli exist.
  const eeAvail = eOverEp !== null;
  const eeCut = eSep !== null && eLat !== null ? 14 : eSep !== null ? 15 : eLat !== null ? 13 : 14;
  const eeHigh = eOverEp !== null && eOverEp >= eeCut;

  // Primary 3 — TR/PASP elevated.
  const trAvail = tr !== null || pasp !== null;
  const trHigh = (tr !== null && tr >= 2.8) || (pasp !== null && pasp >= 35);

  const availPrimary = (eLowAvail ? 1 : 0) + (eeAvail ? 1 : 0) + (trAvail ? 1 : 0);
  if (availPrimary === 0) return null;
  if (availPrimary < 2) return 9; // too sparse to grade

  const abnCount = (eLow ? 1 : 0) + (eeHigh ? 1 : 0) + (trHigh ? 1 : 0);
  if (abnCount === 0) return 0; // Normal diastolic function

  const ea = num(i.eaRatio ?? null);

  // Supplemental LA-pressure markers (count only the ones actually available).
  const larStrain = num(i.larStrain ?? null);
  const pvSD = num(i.pvSD ?? null);
  const lavi = num(i.lavi ?? null);
  const ivrt = num(i.ivrt ?? null);
  const mainFlags: Array<boolean | null> = [
    pvSD !== null ? pvSD <= 0.67 : null,
    larStrain !== null ? larStrain <= 18 : null,
    lavi !== null ? lavi > 34 : null,
  ];
  const mainAvail = mainFlags.filter((f): f is boolean => f !== null);
  // IVRT is the alternative marker, consulted only when none of the three is available.
  const suppAvail = mainAvail.length ? mainAvail : ivrt !== null ? [ivrt <= 70] : [];
  const suppPos = suppAvail.filter(Boolean).length;

  // "Reduced e′ ONLY" branch (e′ abnormal, E/e′ and TR/PASP normal).
  if (eLow && !eeHigh && !trHigh) {
    if (ea !== null && ea <= 0.8) return 1; // impaired relaxation, normal LAP
    if (suppAvail.length === 0) return 9; // E/A > 0.8 (or unknown) but no supplemental data
    if (suppPos === 0) return 1; // normal LAP → Grade 1
    return gradeByEA(ea); // elevated LAP
  }

  // All three abnormal ⇒ elevated LAP; otherwise supplemental markers decide.
  let elevatedLAP: boolean;
  if (abnCount === 3) {
    elevatedLAP = true;
  } else {
    if (suppAvail.length === 0) return 9;
    elevatedLAP = suppPos >= 1;
  }
  if (!elevatedLAP) return 1; // Grade 1, normal LAP
  return gradeByEA(ea);
}
